import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { DELETE, GET, POST, PUT, httpOk } from 'helpers/api'

const EMPTY_FORM = {
	id: null,
	parent_id: null,
	name: '',
	url: '',
	icon: '',
	title: false,
	permission: '',
	status: 1,
}

function makeTree(list) {
	const items = list.map(item => ({ ...item, children: [] }))
	const map = {}
	const roots = []

	items.forEach((item, index) => {
		map[item.id] = index
	})

	items.forEach(item => {
		if (item.parent_id && map[item.parent_id] !== undefined) {
			items[map[item.parent_id]].children.push(item)
		} else if (!item.parent_id) {
			roots.push(item)
		}
	})

	roots.sort((a, b) => a.order_number - b.order_number)
	roots.forEach(root => {
		root.children.sort((a, b) => a.order_number - b.order_number)
	})

	return roots
}

function SortableRootHeader({ item, expanded, onToggle, onAddChild, onEdit, onDelete }) {
	const { t } = useTranslation()
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id })

	return (
		<div
			ref={setNodeRef}
			className={`menu-item-header nav-admin-section-header ${isDragging ? 'nav-admin-dragging' : ''}`}
			style={{ transform: CSS.Transform.toString(transform), transition }}>
			<button type="button" className="nav-admin-drag-handle" {...attributes} {...listeners}>
				<i className="uil uil-draggabledots"></i>
			</button>
			<span className="menu-text flex-grow-1" onClick={onToggle}>
				<i className={item.icon || 'uil-apps'}></i>
				{t(item.name)}
			</span>
			<div className="d-flex align-items-center gap-2">
				<button type="button" className="btn btn-link btn-sm p-0" onClick={onAddChild}>
					<i className="uil uil-plus"></i>
				</button>
				<button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit}>
					<i className="uil uil-edit-alt"></i>
				</button>
				<button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={onDelete}>
					<i className="uil uil-trash-alt"></i>
				</button>
				<i className={`arrow-icon uil-angle-${expanded ? 'up' : 'down'}`} onClick={onToggle}></i>
			</div>
		</div>
	)
}

function SortableChildRow({ item, onEdit, onDelete }) {
	const { t } = useTranslation()
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id, disabled: Boolean(item.title) })

	if (item.title) {
		return (
			<li className="nav-admin-item">
				<div className="second-sidebar-title nav-admin-title-row">
					<div>{t(item.name)}</div>
					<div className="nav-admin-actions">
						<button type="button" className="btn btn-link btn-sm p-0" onClick={() => onEdit(item)}>
							<i className="uil uil-edit-alt"></i>
						</button>
					</div>
				</div>
			</li>
		)
	}

	return (
		<li
			ref={setNodeRef}
			className={`nav-admin-item ${isDragging ? 'nav-admin-dragging' : ''} ${Number(item.status) !== 1 ? 'nav-admin-inactive' : ''}`}
			style={{ transform: CSS.Transform.toString(transform), transition }}>
			<div className="d-flex align-items-center nav-admin-row">
				<button type="button" className="nav-admin-drag-handle" {...attributes} {...listeners}>
					<i className="uil uil-draggabledots"></i>
				</button>
				<div className="d-flex align-items-center flex-grow-1 nav-admin-child-link">
					<i className={item.icon || 'uil-circle'}></i>
					<div className="vertical-menu-div">{t(item.name)}</div>
					{item.url && <span className="nav-admin-url text-muted ms-2">{item.url}</span>}
				</div>
				<div className="nav-admin-actions">
					<button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => onEdit(item)}>
						<i className="uil uil-edit-alt"></i>
					</button>
					<button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => onDelete(item)}>
						<i className="uil uil-trash-alt"></i>
					</button>
				</div>
			</div>
		</li>
	)
}

function Index() {
	const { t } = useTranslation()
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

	const [flatList, setFlatList] = useState([])
	const [expandedIds, setExpandedIds] = useState({})
	const [showModal, setShowModal] = useState(false)
	const [form, setForm] = useState(EMPTY_FORM)
	const [permissions, setPermissions] = useState([])

	const tree = useMemo(() => makeTree(flatList.filter(item => Number(item.status) === 1)), [flatList])
	const parentOptions = useMemo(() => (
		tree.map(item => ({ value: item.id, label: t(item.name) }))
	), [tree, t])

	const permissionOptions = useMemo(() => (
		permissions
			.filter(item => item.value)
			.map(item => ({ value: item.value, label: `${item.value} — ${item.name}` }))
	), [permissions])

	async function getData() {
		const response = await GET('/admin/navigations', {}, { loader: true })
		if (httpOk(response)) {
			setFlatList(response.data.map(item => ({
				...item,
				title: Boolean(item.title),
				status: Number(item.status),
			})))
		}
	}

	async function getPermissions() {
		const response = await GET('/admin/permissions', {}, { loader: false })
		if (httpOk(response)) setPermissions(response.data)
	}

	function toggleExpanded(id) {
		setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }))
	}

	function openModal(item = EMPTY_FORM) {
		setForm({
			...EMPTY_FORM,
			...item,
			parent_id: item.parent_id ?? null,
			title: Boolean(item.title),
			status: item.status === undefined ? 1 : Number(item.status),
		})
		setShowModal(true)
	}

	function closeModal() {
		setShowModal(false)
		setForm(EMPTY_FORM)
	}

	async function saveItem(event) {
		event.preventDefault()

		const payload = {
			...form,
			parent_id: form.parent_id || null,
			title: Boolean(form.title),
			status: form.status ? 1 : -1,
		}

		const response = form.id
			? await PUT('/admin/navigations', payload, { loader: true })
			: await POST('/admin/navigations', payload, { loader: true })

		if (httpOk(response)) {
			closeModal()
			getData()
		}
	}

	async function deleteItem(item) {
		if (!window.confirm(`${t('delete')} "${t(item.name)}"?`)) return

		const response = await DELETE(`/admin/navigations/${item.id}`, {}, { loader: true })
		if (httpOk(response)) getData()
	}

	async function persistReorder(items) {
		await PUT('/admin/navigations/reorder', {
			items: items.map((row, index) => ({
				id: row.id,
				order_number: index + 1,
				parent_id: row.parent_id ?? null,
			})),
		}, { loader: false })
	}

	function reorderLocal(parentId, reordered) {
		setFlatList(prev => {
			const next = [...prev]
			reordered.forEach((item, index) => {
				const rowIndex = next.findIndex(row => row.id === item.id)
				if (rowIndex !== -1) {
					next[rowIndex] = {
						...next[rowIndex],
						order_number: index + 1,
						parent_id: parentId,
					}
				}
			})
			return next
		})
	}

	function handleRootDragEnd(event) {
		const { active, over } = event
		if (!active || !over || active.id === over.id) return

		const oldIndex = tree.findIndex(item => item.id === active.id)
		const newIndex = tree.findIndex(item => item.id === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(tree, oldIndex, newIndex).map(item => ({
			...item,
			parent_id: null,
		}))

		reorderLocal(null, reordered)
		persistReorder(reordered)
	}

	function handleChildDragEnd(rootId, children) {
		return (event) => {
			const { active, over } = event
			if (!active || !over || active.id === over.id) return

			const oldIndex = children.findIndex(item => item.id === active.id)
			const newIndex = children.findIndex(item => item.id === over.id)
			if (oldIndex === -1 || newIndex === -1) return

			const reordered = arrayMove(children, oldIndex, newIndex).map(item => ({
				...item,
				parent_id: rootId,
			}))

			reorderLocal(rootId, reordered)
			persistReorder(reordered)
		}
	}

	useEffect(() => {
		getData()
		getPermissions()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center">
				<div>
					<h4 className="vertical-center mb-1">{t('navigations')}</h4>
					<p className="text-muted mb-0 fz-14">{t('navigations_page_hint')}</p>
				</div>
				<button type="button" className="btn btn-primary" onClick={() => openModal()}>
					<i className="uil uil-plus me-1"></i>
					{t('create')}
				</button>
			</div>

			<div className="row g-3">
				<div className="col-lg-5 col-xl-4">
					<div className="card nav-admin-card">
						<div className="card-body p-0">
							<div id="sidebar-menu" className="nav-admin-sidebar">
								<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRootDragEnd}>
									<SortableContext items={tree.map(item => item.id)} strategy={verticalListSortingStrategy}>
										<ul className="mb-0">
											{tree.map(root => (
												<li key={root.id} className="nav-admin-section">
													<SortableRootHeader
														item={root}
														expanded={Boolean(expandedIds[root.id])}
														onToggle={() => toggleExpanded(root.id)}
														onAddChild={() => openModal({ ...EMPTY_FORM, parent_id: root.id })}
														onEdit={() => openModal(root)}
														onDelete={() => deleteItem(root)}
													/>

													{expandedIds[root.id] &&
														<ul className="nav-admin-children">
															<DndContext
																sensors={sensors}
																collisionDetection={closestCenter}
																onDragEnd={handleChildDragEnd(root.id, root.children)}>
																<SortableContext
																	items={root.children.filter(item => !item.title).map(item => item.id)}
																	strategy={verticalListSortingStrategy}>
																	{root.children.map(child => (
																		<SortableChildRow
																			key={child.id}
																			item={child}
																			onEdit={openModal}
																			onDelete={deleteItem}
																		/>
																	))}
																</SortableContext>
															</DndContext>
														</ul>
													}
												</li>
											))}
										</ul>
									</SortableContext>
								</DndContext>
							</div>
						</div>
					</div>
				</div>

				<div className="col-lg-7 col-xl-8">
					<div className="card">
						<div className="card-body">
							<h5 className="mb-3">{t('navigations_help_title')}</h5>
							<ul className="text-muted mb-0">
								<li>{t('navigations_help_drag')}</li>
								<li>{t('navigations_help_name')}</li>
								<li>{t('navigations_help_permission')}</li>
								<li>{t('navigations_help_title_row')}</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			<Modal show={showModal} onHide={closeModal} centered>
				<form onSubmit={saveItem}>
					<Modal.Header closeButton>
						<Modal.Title>{form.id ? t('edit') : t('create')}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-3">
							<label className="form-label">{t('category')}</label>
							<Select
								isClearable
								options={parentOptions}
								value={parentOptions.find(option => option.value === form.parent_id) || null}
								onChange={(option) => setForm({ ...form, parent_id: option?.value ?? null })}
								placeholder={t('navigations_top_level')}
							/>
						</div>
						<div className="mb-3">
							<label className="form-label">{t('name')} (i18n)</label>
							<input
								type="text"
								className="form-control"
								value={form.name}
								onChange={(event) => setForm({ ...form, name: event.target.value })}
								required
							/>
						</div>
						<div className="mb-3">
							<label className="form-label">URL</label>
							<input
								type="text"
								className="form-control"
								value={form.url || ''}
								onChange={(event) => setForm({ ...form, url: event.target.value })}
								placeholder="/example-page"
							/>
						</div>
						<div className="mb-3">
							<label className="form-label">{t('icon')}</label>
							<input
								type="text"
								className="form-control"
								value={form.icon || ''}
								onChange={(event) => setForm({ ...form, icon: event.target.value })}
								placeholder="uil-chart-growth"
							/>
						</div>
						<div className="mb-3">
							<label className="form-label">{t('permissions')}</label>
							<Select
								isClearable
								options={permissionOptions}
								value={
									form.permission
										? permissionOptions.find(option => option.value === form.permission) || { value: form.permission, label: form.permission }
										: null
								}
								onChange={(option) => setForm({ ...form, permission: option?.value ?? '' })}
								placeholder="integrations"
							/>
							<input
								type="text"
								className="form-control mt-2"
								value={form.permission || ''}
								onChange={(event) => setForm({ ...form, permission: event.target.value })}
								placeholder="N/A"
								required
							/>
						</div>
						<div className="d-flex justify-content-between align-items-center mb-3">
							<label className="form-label mb-0">{t('navigations_section_title')}</label>
							<div className="form-check form-switch">
								<input
									type="checkbox"
									className="form-check-input"
									checked={Boolean(form.title)}
									onChange={(event) => setForm({ ...form, title: event.target.checked })}
								/>
							</div>
						</div>
						<div className="d-flex justify-content-between align-items-center">
							<label className="form-label mb-0">{t('active')}</label>
							<div className="form-check form-switch">
								<input
									type="checkbox"
									className="form-check-input"
									checked={Number(form.status) === 1}
									onChange={(event) => setForm({ ...form, status: event.target.checked ? 1 : -1 })}
								/>
							</div>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<button type="button" className="btn btn-light" onClick={closeModal}>{t('cancel')}</button>
						<button type="submit" className="btn btn-primary">{t('save')}</button>
					</Modal.Footer>
				</form>
			</Modal>

			<style>{`
				.nav-admin-card { overflow: hidden; }
				.nav-admin-sidebar { background: var(--dark-surface, #fff); min-height: 520px; }
				.nav-admin-section + .nav-admin-section { border-top: 1px solid var(--dark-border, #eef2f7); }
				.nav-admin-section-header { cursor: default; gap: 8px; }
				.nav-admin-section-header .menu-text { cursor: pointer; }
				.nav-admin-children { list-style: none; padding: 0; margin: 0; background: var(--dark-surface-2, #f8f9fb); }
				.nav-admin-item { list-style: none; }
				.nav-admin-row { gap: 8px; padding: 8px 12px; min-height: 42px; }
				.nav-admin-child-link { padding-left: 4px; }
				.nav-admin-drag-handle {
					border: 0;
					background: transparent;
					color: var(--dark-text-muted, #98a6ad);
					padding: 0;
					line-height: 1;
					cursor: grab;
					flex-shrink: 0;
				}
				.nav-admin-drag-handle:active { cursor: grabbing; }
				.nav-admin-actions { display: flex; align-items: center; flex-shrink: 0; }
				.nav-admin-title-row {
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding-right: 12px;
				}
				.nav-admin-url {
					font-size: 11px;
					max-width: 120px;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
				.nav-admin-dragging { opacity: 0.75; z-index: 2; position: relative; }
				.nav-admin-inactive { opacity: 0.55; }
			`}</style>
		</>
	)
}

export default Index
